import {
  Autocomplete,
  Avatar,
  Box,
  Card,
  CardHeader,
  Grid,
  Grow,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  AdsAccountFormFields,
  Brand,
  ChangeEventType,
  FbAdsAccountData,
  IFacebookUser,
  Page,
} from '../../../../types';
import {
  fetchBusinessFbPages,
  fetchFbAdsAccount,
  fetchFbPageIgAccounts,
} from '../../../../services/ads';
import { adsProvider } from '../../../../utils/constants/facebookAds';
import { FloatingButton, PrimaryButton } from '../../../Buttons';
import { DiySettings } from '../../../../types/IDiy';
import { getOffsetLabel } from '../../../../utils/timezones';
import Spinner from '../../../Spinner';
import { deepOrange } from '@mui/material/colors';
import ConnectFacebookAccountForm from '../../ConnectFacebookAccountForm';
import { errorMessageParser } from '../../../../utils/helpers/ToastHelper';
import { useDispatch } from 'react-redux';
import { setBrand, setLocation, toggleAlert } from '../../../../redux/actions';
import { useForm } from 'react-hook-form';
import { editBrand, getSingleBrand } from '../../../../services/brand';
import { createOrUpdateDiySettings } from '../../../../services/diy';

interface FacebookAccountsFormProps {
  brand: Brand;
  location: Brand;
  currentUserId: string;
  diySettings: DiySettings;
  setDiySettings: Dispatch<SetStateAction<DiySettings>>;
  franchisor: Brand;
  role: string;
  isAdmin: boolean;
}

const FacebookAccountsForm: React.FC<FacebookAccountsFormProps> = ({
  brand,
  location,
  franchisor,
  currentUserId,
  diySettings,
  setDiySettings,
  role,
  isAdmin,
}) => {
  const {
    formState: { errors },
    register,
    handleSubmit,
    clearErrors,
    setValue,
  } = useForm({ mode: 'onSubmit' });
  const dispatch = useDispatch();
  const isLocation = (location || brand)?.__type === 'location';
  const [loading, setLoading] = useState<boolean>(false);
  const [fbAdsAccounts, setFbAdsAccounts] = useState<AdsAccountFormFields[]>(
    [],
  );
  const [businessId, setBusinessId] = useState<string>('');
  const [facebookPages, setFacebookPages] = useState<Page[]>([]);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [igAccounts, setIgAccounts] = useState<Page[]>([]);
  const [igAccountLoading, setIgAccountLoading] = useState<boolean>(false);
  const [facebookAccount, setFacebookAccount] = useState<IFacebookUser>(null);
  const [updateAccountLoading, setUpdateAccountLoading] =
    useState<boolean>(false);
  const [diySettingForm, setDiySettingForm] = useState<DiySettings>(null);
  const [brandForm, setBrandForm] = useState<Brand>(null);

  useEffect(() => {
    getBrand();
  }, [location, brand]);

  useEffect(() => {
    if (brandForm?.facebookBusinessIds?.length > 0) {
      getFbAdsAccounts(brandForm, currentUserId);
      setBusinessId(brandForm?.facebookBusinessIds[0] || '');
      setValue('facebookBusinessIds', brandForm?.facebookBusinessIds?.[0]);
    }
  }, [brandForm?.facebookBusinessIds]);

  useEffect(() => {
    if (diySettings) setDiySettingForm(diySettings);
  }, [diySettings]);

  useEffect(() => {
    if (
      (isLocation
        ? diySettingForm?.allowSetupOwnAccount
          ? brandForm
          : franchisor
        : brandForm
      )?.facebookBusinessIds?.length > 0
    ) {
      getFacebookPages();
    }
  }, [
    (isLocation
      ? diySettingForm?.allowSetupOwnAccount
        ? brandForm
        : franchisor
      : brandForm
    )?.facebookBusinessIds,
  ]);

  useEffect(() => {
    if (brandForm && diySettingForm?.facebookPage?.fbPageId) {
      getIgAccount();
    }
  }, [diySettingForm?.facebookPage?.fbPageId, brandForm]);

  useEffect(() => {
    if (diySettingForm?.facebookPage?.fbPageId) {
      setValue('facebookPage', diySettingForm?.facebookPage?.fbPageId);
    }
  }, [diySettingForm?.facebookPage?.fbPageId]);

  useEffect(() => {
    if (diySettingForm?.fbAdAccount?.accountId) {
      setValue(
        'adsAccount',
        `${diySettingForm?.fbAdAccount?.name} (${diySettingForm?.fbAdAccount?.accountId})`,
      );
    }
  }, [diySettingForm?.fbAdAccount?.accountId]);

  const getFbAdsAccounts = async (brand: Brand, userId: string) => {
    try {
      setLoading(true);

      const response = await fetchFbAdsAccount(
        brand?.facebookBusinessIds,
        brand?._id,
        userId,
      );

      if (response?.data) {
        const newFormvalues = response.data.map(
          (adsAccount: FbAdsAccountData) => {
            return {
              accountId: adsAccount.account_id,
              name: adsAccount.name,
              currency: adsAccount.currency,
              provider: adsProvider.FACEBOOK,
              timezone: adsAccount.timezone_name,
              timezoneUtcOffset: adsAccount.timezone_offset_hours_utc,
            };
          },
        );

        setFbAdsAccounts(newFormvalues);
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getFacebookPages = async () => {
    try {
      setPageLoading(true);

      const response = await fetchBusinessFbPages(
        (isLocation
          ? diySettingForm?.allowSetupOwnAccount
            ? brandForm
            : franchisor
          : brandForm
        )?.facebookBusinessIds || [],
        brandForm?._id,
      );

      setFacebookPages(response?.data || []);
    } catch (error: any) {
      console.log(error.message);
    } finally {
      setPageLoading(false);
    }
  };

  const getIgAccount = async () => {
    try {
      setIgAccountLoading(true);

      const response = await fetchFbPageIgAccounts(
        diySettingForm?.facebookPage?.fbPageId,
        brandForm?._id,
      );

      setIgAccounts(response?.data || []);
    } catch (error: any) {
      console.log(error.message);
      setIgAccounts([]);
      setDiySettingForm({
        ...diySettingForm,
        instagramAccount: null,
      });
    } finally {
      setIgAccountLoading(false);
    }
  };

  const getBrand = async () => {
    try {
      const response: Brand = await getSingleBrand((location || brand)?._id);

      setBrandForm(response);
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const handleOnSubmit = async () => {
    try {
      setUpdateAccountLoading(true);

      let temp: Brand = {
        ...brandForm,
        facebookBusinessIds: [businessId],
      };

      const updatedBrand = await editBrand(temp, brandForm?._id);

      if (location) {
        dispatch(setLocation(updatedBrand.updatedBrand));
      } else {
        dispatch(setBrand(updatedBrand.updatedBrand));
      }

      const updateDiySettings = await createOrUpdateDiySettings(
        brandForm?._id,
        diySettingForm,
      );

      setDiySettings(updateDiySettings.data);

      dispatch(
        toggleAlert({ toggle: true, message: 'Set up account successfully' }),
      );
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setUpdateAccountLoading(false);
    }
  };

  const handleOnAddBusinessId = () => {
    if (!businessId) {
      return dispatch(
        toggleAlert({
          toggle: true,
          message: 'Business ID is required',
          type: 'error',
        }),
      );
    }

    let temp: Brand = {
      ...brandForm,
      facebookBusinessIds: [businessId],
    };

    setBrandForm(temp);

    if (temp?.facebookBusinessIds?.length === 0) {
      setDiySettingForm({
        ...diySettingForm,
        facebookPage: null,
        instagramAccount: null,
      });
    }
  };

  const handleOnSelectAdAccount = (value: AdsAccountFormFields) => {
    setDiySettingForm({
      ...diySettingForm,
      fbAdAccount: value,
    });
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Grid container spacing={2}>
            {diySettingForm?.allowSetupOwnAccount ? (
              <Grid item xs={12}>
                <ConnectFacebookAccountForm
                  userId={
                    brandForm?.agency?.defaultUser?._id ||
                    diySettingForm?.defaultUser?._id
                  }
                  role={role}
                  diySettings={diySettingForm}
                  setDiySettings={setDiySettingForm}
                  isLoggedInUser={
                    brandForm?.agency?.defaultUser?._id === currentUserId ||
                    diySettingForm?.defaultUser?._id === currentUserId
                  }
                  facebookAccount={facebookAccount}
                  setFacebookAccount={setFacebookAccount}
                />
              </Grid>
            ) : null}

            {(diySettingForm?.allowSetupOwnAccount && facebookAccount) ||
            !diySettingForm?.allowSetupOwnAccount ? (
              <>
                <Grid item xs={12}>
                  <TextField
                    {...register('facebookBusinessIds', {
                      required: true,
                    })}
                    fullWidth
                    required
                    variant="standard"
                    size="small"
                    type="text"
                    id="facebookBusinessIds"
                    name="facebookBusinessIds"
                    label="Facebook Business ID"
                    onChange={(e: ChangeEventType) => {
                      clearErrors('facebookBusinessIds');
                      setValue('facebookBusinessIds', e.target.value);
                      setBusinessId(e.target.value);
                    }}
                    value={businessId}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        '&:has(> input:-webkit-autofill)': {
                          backgroundColor: 'rgb(232, 240, 254)',
                        },
                      },
                    }}
                    InputProps={{
                      sx: { borderRadius: '49px' },
                      endAdornment: (
                        <InputAdornment position="end">
                          <PrimaryButton
                            type="button"
                            variant="text"
                            title="Connect"
                            handleOnClick={handleOnAddBusinessId}
                            disabled={
                              (isAdmin &&
                                diySettingForm?.allowSetupOwnAccount) ||
                              (!isAdmin &&
                                diySettingForm?.allowSetupOwnAccount &&
                                diySettingForm?.defaultUser?._id !==
                                  currentUserId)
                            }
                          />
                        </InputAdornment>
                      ),
                    }}
                    disabled={
                      (isAdmin && diySettingForm?.allowSetupOwnAccount) ||
                      (!isAdmin &&
                        diySettingForm?.allowSetupOwnAccount &&
                        diySettingForm?.defaultUser?._id !== currentUserId)
                    }
                    error={!!errors?.facebookBusinessIds}
                    helperText={
                      !!errors?.facebookBusinessIds ? (
                        errors?.facebookBusinessIds &&
                        'Facebook Business ID is required'
                      ) : (
                        <Fragment>
                          <Box
                            component="a"
                            target="_blank"
                            href="https://m.facebook.com/help/1181250022022158"
                          >
                            Where can I find my Facebook business ID?
                          </Box>
                        </Fragment>
                      )
                    }
                  />
                </Grid>

                {fbAdsAccounts?.length > 0 &&
                brandForm?.facebookBusinessIds?.length > 0 ? (
                  <Grow in={true}>
                    <Grid item xs={12}>
                      <Autocomplete
                        id="adsAccount"
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={option.accountId}>
                              <Box
                                component="div"
                                sx={{ flexDirection: 'column' }}
                              >
                                <Typography variant="body1">
                                  {option.name}
                                </Typography>

                                <Typography variant="caption">
                                  {option.accountId}
                                </Typography>
                              </Box>
                            </li>
                          );
                        }}
                        options={fbAdsAccounts?.sort(
                          (a, b) => -b.name.localeCompare(a.name),
                        )}
                        value={
                          fbAdsAccounts?.find(
                            (account: AdsAccountFormFields) =>
                              account.accountId ===
                              diySettingForm?.fbAdAccount?.accountId,
                          ) || null
                        }
                        getOptionLabel={(option) =>
                          `${option?.name} (${option?.accountId})`
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            {...register('adsAccount', {
                              required: true,
                            })}
                            required
                            variant="standard"
                            label="Ad Accounts"
                            name="adsAccount"
                            placeholder="Select Your Ads Accounts"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors?.adsAccount}
                            helperText={
                              !!errors?.adsAccount ? (
                                errors?.adsAccount &&
                                'Facebook ad account is required'
                              ) : diySettingForm?.fbAdAccount?.timezone ? (
                                <Fragment>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    component="div"
                                  >
                                    Timezone:
                                    <Typography
                                      variant="caption"
                                      display="inline"
                                      ml={1}
                                    >
                                      {`${getOffsetLabel(
                                        diySettingForm?.fbAdAccount
                                          ?.timezoneUtcOffset,
                                      )} ${
                                        diySettingForm?.fbAdAccount?.timezone
                                      }`}
                                    </Typography>
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    component="div"
                                  >
                                    Currency:
                                    <Typography
                                      variant="caption"
                                      display="inline"
                                      ml={1}
                                    >
                                      {`${diySettingForm?.fbAdAccount?.currency}`}
                                    </Typography>
                                  </Typography>
                                </Fragment>
                              ) : null
                            }
                          />
                        )}
                        onChange={(e: any, value: any, reason: string) => {
                          let selectedAccount: any = null;
                          if (reason !== 'clear') {
                            selectedAccount = value;
                          }

                          clearErrors('adsAccount');
                          handleOnSelectAdAccount(selectedAccount);
                          setValue(
                            'adsAccount',
                            `${value?.name} (${value?.accountId})`,
                          );
                        }}
                        disabled={
                          (isAdmin && diySettingForm?.allowSetupOwnAccount) ||
                          (!isAdmin &&
                            diySettingForm?.allowSetupOwnAccount &&
                            diySettingForm?.defaultUser?._id !== currentUserId)
                        }
                      />
                    </Grid>
                  </Grow>
                ) : loading ? (
                  <Grid
                    item
                    xs={12}
                    sx={{ justifyContent: 'center', display: 'flex' }}
                  >
                    <Spinner />
                  </Grid>
                ) : null}

                {brandForm?.facebookBusinessIds?.length > 0 &&
                facebookPages?.length > 0 &&
                !pageLoading ? (
                  <>
                    <Grow in={true}>
                      <Grid item xs={12}>
                        <Autocomplete
                          id="fbPage"
                          renderOption={(props, option) => {
                            return (
                              <Box
                                component="li"
                                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                {...props}
                              >
                                <Avatar
                                  src={option.imageUrl}
                                  sx={{ marginRight: '10px' }}
                                />

                                <Box
                                  component="div"
                                  sx={{ flexDirection: 'column' }}
                                >
                                  <Typography variant="body1">
                                    {option.name}
                                  </Typography>

                                  <Typography variant="caption">
                                    {option.fbPageId}
                                  </Typography>

                                  {!option.accessToken ? (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'red', fontWeight: 'bold' }}
                                      component="div"
                                    >
                                      You do not have the necessary permissions
                                      to use this page
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            );
                          }}
                          options={facebookPages?.sort(
                            (a, b) => -b.name?.localeCompare(a.name),
                          )}
                          loading={pageLoading}
                          value={
                            facebookPages?.find(
                              (page: Page) =>
                                page.fbPageId ===
                                (diySettingForm || diySettings)?.facebookPage
                                  ?.fbPageId,
                            ) || null
                          }
                          getOptionLabel={(option) => option.name}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              {...register('facebookPage', {
                                required: true,
                              })}
                              variant="standard"
                              label="Facebook Page"
                              name="facebookPage"
                              fullWidth
                              size="small"
                              placeholder="Select a Facebook page"
                              required
                              InputLabelProps={{ shrink: true }}
                              error={!!errors?.facebookPage}
                              helperText={
                                errors?.facebookPage &&
                                'Facebook page is required'
                              }
                            />
                          )}
                          onChange={(e: any, value: any, reason: string) => {
                            clearErrors('facebookPage');
                            if (reason !== 'clear') {
                              setDiySettingForm({
                                ...diySettingForm,
                                facebookPage: value,
                                instagramAccount: null,
                              });
                            } else {
                              setDiySettingForm({
                                ...diySettingForm,
                                facebookPage: null,
                                instagramAccount: null,
                              });

                              setIgAccounts([]);
                            }
                            setValue('facebookPage', value?.fbPageId);
                          }}
                          disabled={
                            (isAdmin && diySettingForm?.allowSetupOwnAccount) ||
                            (!isAdmin &&
                              diySettingForm?.allowSetupOwnAccount &&
                              diySettingForm?.defaultUser?._id !==
                                currentUserId)
                          }
                          getOptionDisabled={(option) => {
                            return !option.accessToken;
                          }}
                        />
                      </Grid>
                    </Grow>

                    {diySettingForm?.facebookPage ? (
                      <>
                        <Grow in={true}>
                          <Grid item xs={12} lg={6} mt={1}>
                            <Card>
                              <CardHeader
                                avatar={
                                  <Avatar
                                    src={
                                      facebookPages?.find(
                                        (page: Page) =>
                                          page.fbPageId ===
                                          diySettingForm?.facebookPage
                                            ?.fbPageId,
                                      )?.imageUrl
                                    }
                                    alt={diySettingForm?.facebookPage?.name}
                                    sx={{ bgcolor: deepOrange[500] }}
                                  />
                                }
                                title={diySettingForm?.facebookPage?.name}
                                subheader={
                                  diySettingForm?.facebookPage?.fbPageId
                                }
                              />
                            </Card>
                          </Grid>
                        </Grow>

                        {igAccounts.length > 0 &&
                        !igAccountLoading &&
                        !loading &&
                        !pageLoading ? (
                          <>
                            <Grow in>
                              <Grid item xs={12}>
                                <Autocomplete
                                  id="igAccount"
                                  renderOption={(props, option) => {
                                    return (
                                      <Box
                                        component="li"
                                        sx={{
                                          '& > img': { mr: 2, flexShrink: 0 },
                                        }}
                                        {...props}
                                      >
                                        <Avatar
                                          src={option.imageUrl}
                                          sx={{ marginRight: '10px' }}
                                        />

                                        <Box
                                          component="div"
                                          sx={{ flexDirection: 'column' }}
                                        >
                                          <Typography variant="body1">
                                            {option.instagramUsername}
                                          </Typography>

                                          <Typography variant="caption">
                                            {option.instagramId}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  }}
                                  options={igAccounts?.sort(
                                    (a, b) =>
                                      -b.instagramUsername?.localeCompare(
                                        a.instagramUsername,
                                      ),
                                  )}
                                  value={
                                    diySettingForm?.instagramAccount || null
                                  }
                                  getOptionLabel={(option) =>
                                    option.instagramUsername
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="standard"
                                      label="Instagram Account"
                                      fullWidth
                                      size="small"
                                      placeholder="Select a Instagram Account"
                                      InputLabelProps={{ shrink: true }}
                                      //error={errors?.agency ? true : false}
                                      //helperText={errors?.agency ? errors?.agency : ''}
                                    />
                                  )}
                                  onChange={(
                                    e: any,
                                    value: any,
                                    reason: string,
                                  ) => {
                                    let selectedAccount: any = null;
                                    if (reason !== 'clear') {
                                      selectedAccount = value;
                                    }

                                    setDiySettingForm({
                                      ...diySettingForm,
                                      instagramAccount: selectedAccount,
                                    });
                                  }}
                                  disabled={
                                    (isAdmin &&
                                      diySettingForm?.allowSetupOwnAccount) ||
                                    (!isAdmin &&
                                      diySettingForm?.allowSetupOwnAccount &&
                                      diySettingForm?.defaultUser?._id !==
                                        currentUserId)
                                  }
                                />
                              </Grid>
                            </Grow>

                            {diySettingForm?.instagramAccount ? (
                              <Grow in>
                                <Grid item xs={12} lg={6} mt={1}>
                                  <Card
                                    sx={{
                                      border:
                                        !igAccounts
                                          ?.map(
                                            (igAccount: Page) =>
                                              igAccount.instagramId,
                                          )
                                          ?.includes(
                                            diySettingForm?.instagramAccount
                                              ?.instagramId,
                                          ) &&
                                        !igAccountLoading &&
                                        !pageLoading
                                          ? '2px solid red'
                                          : '',
                                    }}
                                  >
                                    <CardHeader
                                      avatar={
                                        <Avatar
                                          src={
                                            igAccounts?.find(
                                              (igAccount: Page) =>
                                                igAccount.instagramId ===
                                                diySettingForm?.instagramAccount
                                                  ?.instagramId,
                                            )?.imageUrl
                                          }
                                        />
                                      }
                                      title={
                                        diySettingForm?.instagramAccount
                                          ?.instagramUsername
                                      }
                                      subheader={
                                        diySettingForm?.instagramAccount
                                          ?.instagramId
                                      }
                                    />
                                  </Card>

                                  {!igAccounts
                                    ?.map(
                                      (igAccount: Page) =>
                                        igAccount.instagramId,
                                    )
                                    ?.includes(
                                      diySettingForm?.instagramAccount
                                        ?.instagramId,
                                    ) &&
                                  !igAccountLoading &&
                                  !pageLoading ? (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'red', fontWeight: 'bold' }}
                                    >
                                      Invalid Instagram account. The user may
                                      have changed the Instagram account linked
                                      to the Facebook page.
                                    </Typography>
                                  ) : null}
                                </Grid>
                              </Grow>
                            ) : null}
                          </>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ paddingTop: '20px' }}>
        <FloatingButton
          title="Save"
          onClick={handleSubmit(handleOnSubmit)}
          loading={updateAccountLoading}
          bottom="60px"
          fixed
        />
      </Box>
    </Box>
  );
};

export default FacebookAccountsForm;
